import {createMuiTheme} from "@material-ui/core";
import {fade} from '@material-ui/core/styles/colorManipulator';

import {circularBold, robotoBold, robotoRegular} from "./fonts";
import {themeVariables} from "./theme-variables";

export const theme = createMuiTheme({
    palette: {
        primary: {
            main: '#184287'
        },
        secondary: {
            main:'#00954c'
        },
        error: {
            main: '#dd413a'
        },
        success: {
            main: '#62b337'
        },
        text: {
            primary: '#000',
            disabled: '#7f7f7f'
        },
        background: {
            default: '#fff'
        }
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: 16,
        h2: {
            fontSize: 26,
            fontWeight: 700,
            letterSpacing: 0,
            lineHeight: 1.15
        },
        h3: {
            fontFamily: '"LL Circular Bold Sub", "Helvetica", "Arial", sans-serif',
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: 0,
            lineHeight: 1.25,
        },
        h5: {
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: 0,
            lineHeight: 1.25
        },
        h6: {
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: 0,
            lineHeight: 1.25
        },
        subtitle2: {
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: 0,
            lineHeight: 1.4
        },
        body1: {
            fontSize: 16,
            letterSpacing: 0.2
        },
        body2: {
            fontSize: 12,
            letterSpacing: 0,
            lineHeight: 1.334
        },
        button: {
            fontFamily: '"LL Circular Bold Sub", "Helvetica", "Arial", sans-serif',
            fontWeight: 700,
            textTransform: 'none',
            letterSpacing: 0
        }
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1160,
            xl: 1920
        }
    },
    shape: {
        borderRadius: 0
    }
});

theme.typography.h1 = {
    fontFamily: '"LL Circular Bold Sub", "Helvetica", "Arial", sans-serif',
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: 0,
    lineHeight: 1.15,

    [theme.breakpoints.up('md')]: {
        fontSize: 40,
        lineHeight: 1.25,
    }
};

theme.typography.h4 = {
    fontFamily: '"LL Circular Bold Sub", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 0,
    lineHeight: 1.25,

    [theme.breakpoints.up('md')]: {
        fontSize: 16
    }
};

theme.typography.subtitle1 = {
    fontFamily: '"LL Circular Bold Sub", "Helvetica", "Arial", sans-serif',
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: 0,
    lineHeight: 1.4,

    [theme.breakpoints.up('md')]: {
        fontSize: 24,
        lineHeight: 1.25,
    }
};

theme.props = {

    MuiContainer: {
        maxWidth: 'lg'
    },
    MuiButton: {
        variant: 'contained',
        color: 'primary',
        disableElevation: true,
        size: 'large'
    },
    MuiIconButton: {
      disableRipple: true,
      disableFocusRipple: true
    },
    MuiCheckbox: {
      color: 'primary'
    },
    MuiAvatar: {
        variant: 'square'
    }

};

theme.overrides = {

    MuiCssBaseline: {
        '@global': {
            '@font-face': [circularBold, robotoRegular, robotoBold],
            '.main-background': {
                background: fade(theme.palette.primary.main, 0.1)
            },
            '.OTSubscriberContainer ': {
                paddingBottom: '56.25%'
            }
        },
    },

    MuiButton: {

        text: {
            "&.button--white": {
                color: theme.palette.common.white,

                "&:hover": {
                    color: fade(theme.palette.common.white, themeVariables.alpha.buttonHover)
                }
            }
        },

        contained: {
            "&.button--white": {
                background: theme.palette.common.white,
                color: theme.palette.primary.main,

                "&:hover": {
                    background: fade(theme.palette.common.white, themeVariables.alpha.buttonHover)
                }
            }
        }

    },

    MuiDialog: {
        paper: {
            border: '2px solid',
            borderColor: theme.palette.common.black,
        }
    },

    MuiIconButton: {
        root: {
            borderRadius: 0,

            "&.form--white": {
                color: theme.palette.common.white
            }
        }
    },

    MuiInput: {
        root: {
            "&.form--white": {
                color: theme.palette.common.white
            }
        },
        underline: {
            '&::before': {
                borderColor: fade(theme.palette.primary.main, themeVariables.alpha.formDefault),
            },
            "&.form--white": {
                '&::before': {
                    borderColor: fade(theme.palette.common.white, themeVariables.alpha.formDefault),
                },
                "&:hover:not(.Mui-disabled)": {
                    '&::before': {
                        borderColor: theme.palette.common.white
                    },
                },
                '&::after': {
                    borderColor: theme.palette.common.white
                }
            }
        }
    },

    MuiInputLabel: {
        root: {
            color: fade(theme.palette.primary.main, themeVariables.alpha.formDefault),

            "&.form--white": {
                color: fade(theme.palette.common.white, themeVariables.alpha.formDefault),

                '&$focused': {
                    color: theme.palette.common.white
                }
            }
        }
    },

    MuiFilledInput: {
      root: {
          backgroundColor: fade(theme.palette.common.white, 0.8)
      },
    multiline: {
          paddingTop: 10,
    }
    },

    MuiSelect: {
        icon: {
            "&.form--white": {
                color: theme.palette.common.white
            }
        }
    },

    MuiFormControlLabel: {
        root: {
            color: fade(theme.palette.primary.main, themeVariables.alpha.formDefault),
        }
    },

    MuiChip: {
        root: {
            '&.chip--white': {
                color: theme.palette.common.white,
                borderColor: fade(theme.palette.common.white, themeVariables.alpha.formDefault)
            },

            '&.chip--select': {
                marginRight: theme.spacing(1),
                marginBottom: theme.spacing(1)
            }
        }
    },

    MuiTypography : {
        gutterBottom: {
            marginBottom: '0.35em'
        }
    },

    MuiSvgIcon: {
        fontSizeSmall: {
            fontSize: 16
        }
    },

    MuiCircularProgress: {
        root: {
            '&:focus': {
                outline: 0
            }
        }
    }
};